import React from 'react';

// 历史加工数据 (xcs2.7) 的词表与小徽标. 词表来自源数据实测 (doc/数据资产/历史加工数据-实施方案.md §1).

// 加工模式名不在这里维护: 走 utils/mmodeText 的 useMModeText() 从 mm_mode 字典取 (SSOT).

// 差评原因码 (源侧大小写两套拼写, 入库已归一小写下划线).
export const EVAL_OPTION_LABEL: Record<string, string> = {
    not_fully_cut_through: '未切透', cannot_cut_through: '无法切穿',
    too_shallow_engraving: '雕刻太浅', carving_too_shallow: '刻字太浅',
    too_deep_engraving: '雕刻太深', carving_too_deep: '刻字太深',
    yellowing: '发黄', yellowed: '发黄', burnt_edges: '烧焦边缘', burned_edges: '烧焦边缘',
};

// 无法评估的六类原因码 (与打标服务 domain/prompts/scene.py 的 UNABLE_REASON_PRIORITY 同序).
// 顺序即优先级: 一张图命中多条时打标服务只取最靠前的那条, 所以这里不会出现组合值。
export const UNABLE_REASON_LABEL: Record<string, string> = {
    not_a_result_photo: '非成品照',
    not_this_run: '非本次产出',
    occluded: '被遮挡',
    workpiece_too_small: '工件太小',
    poor_photo_quality: '画质太差',
    no_processing_content: '无加工内容',
};
export const unableReasonText = (r: string) => UNABLE_REASON_LABEL[r] || r;

export function EvaluateBadge({ type, compact }: { type: string; compact?: boolean }) {
    if (type === 'like') return <span className={`inline-block rounded text-[11px] whitespace-nowrap bg-emerald-50 text-emerald-600 ${compact ? 'px-1.5 py-0.5 bg-emerald-500 text-white' : 'px-1.5 py-0.5'}`}>满意</span>;
    if (type === 'dislike') return <span className={`inline-block rounded text-[11px] whitespace-nowrap ${compact ? 'px-1.5 py-0.5 bg-rose-500 text-white' : 'px-1.5 py-0.5 bg-rose-50 text-rose-600'}`}>不满意</span>;
    if (compact) return null;
    return <span className="text-slate-300 text-[11px]">无反馈</span>;
}

export function fmtOccurred(ts: number) {
    if (!ts) return '—';
    const d = new Date(ts * 1000);
    const p = (x: number) => String(x).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

// 元素参数表要展示的键 (顺序即列序).
export const ELEMENT_COLS: [string, string][] = [
    ['elementType', '类型'], ['processingType', '工艺'], ['lightSource', '光源'], ['power', '功率'], ['speed', '速度'],
    ['repeat', '次数'], ['dpi', 'DPI'], ['density', '密度'], ['bitmapMode', '位图模式'], ['materialType', '材料类型'], ['laserValue', 'laser%'],
];

// ---------------- 耗材还原 (2026-09-20) ----------------

// 还原三组词表和上面的 EVAL_OPTION_LABEL / UNABLE_REASON_LABEL 同类: 都是**上游导出包固定给的**
// 枚举, 平台不产生也不维护 —— 所以跟着它们放常量, 不进 dict。
// (dict 是给"平台自己维护、会改"的词表用的, 如加工模式/工艺类型/参数字段枚举; 把这三组塞进去
//  只会往字典管理的「其他」组堆三个没人会改的类目。2026-09-20 一度入库, 已撤。)
export const RESTORE_FINISH_LABEL: Record<string, string> = {
    matte: '哑光', glossy: '亮面', mixed: '混合表面',
};
// fail 叫「待复核」而不是「不合格」: 导出包 README 明确 qc 是复核优先级, 不是判废依据。
export const QC_VERDICT_LABEL: Record<string, string> = {
    ok: '通过', minor: '轻微瑕疵', fail: '待复核',
};
// 源值本是中文 (达标/不确定/不达标), 落库归一成 code, 这里再译回来。
export const RESTORE_LABEL_CAT: Record<string, string> = {
    pass: '达标', uncertain: '不确定', reject: '不达标',
};

const RESTORE_LABELS: Record<string, Record<string, string>> = {
    restore_finish: RESTORE_FINISH_LABEL,
    qc_verdict: QC_VERDICT_LABEL,
    restore_label: RESTORE_LABEL_CAT,
};

/** code → 中文; 认不出的回退原 code, 不显示空白 */
export function restoreText(category: string, code?: string, fallback = '—'): string {
    if (!code) return fallback;
    return RESTORE_LABELS[category]?.[code] || code;
}

/** 质检结论的配色: fail 只是"复核优先级高", 不是判废, 所以用琥珀而非红 */
const QC_CLS: Record<string, string> = {
    ok: 'bg-emerald-50 text-emerald-600',
    minor: 'bg-sky-50 text-sky-600',
    fail: 'bg-amber-50 text-amber-600',
};

export function QCBadge({ verdict, text }: { verdict: string; text: string }) {
    if (!verdict) return null;
    return (
        <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] whitespace-nowrap ${QC_CLS[verdict] || 'bg-slate-100 text-slate-500'}`}>
            {text}
        </span>
    );
}

/** 「已还原」角标: 图墙卡片与表格行上标一下, 与筛选里的"已还原"同一口径 */
export function RestoredBadge({ compact }: { compact?: boolean }) {
    return (
        <span className={`inline-block rounded whitespace-nowrap bg-violet-50 text-violet-600 ${compact ? 'px-1 py-0.5 text-[10px]' : 'px-1.5 py-0.5 text-[11px]'}`}
            title="已做耗材还原: 有模型反推出的加工前耗材图">
            已还原
        </span>
    );
}
